/**
 * WHAT LEVERI UNDERSTOOD — and, where it is not sure, what it is asking.
 *
 * This is the only surface in the product where a machine's reading of a
 * sentence is shown to an accountant, so it is written to be READABLE AS A
 * PROPOSAL and never as a finding. It states what Leveri took the request to
 * mean, what it would do, what that capability will NOT conclude, and which
 * reader produced the reading — before anything has run.
 *
 * EVERY WORD ON IT IS THE SERVER'S. The disposition, the confidence band and
 * its tone, the sentence, the steps, the boundaries, the question, the reason
 * it will not perform the work, and the reading of the moment it was made —
 * all decided in `@leveri/intent` and sent already settled. This file branches
 * on which SECTION to draw and on nothing else.
 *
 * WHAT IS DELIBERATELY ABSENT: a confidence score, a percentage, a progress
 * stage, an "accept this interpretation" action. The first two are numbers
 * nobody can act on; the third would be a screen inventing an approval the
 * record cannot carry. Accepting the plan IS starting the work, and that is
 * the governed act below, unchanged.
 */
import { useState } from "react";
import { WorkActionResponse, type Interpretation, type WorkFile } from "@leveri/contracts";
import { Citation, Definitions, Label, Marker, Notice, Reading, RuledList, Section, SectionHead, Statement, markerTone } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";

export interface IntentSectionProps {
  work: WorkFile;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function IntentSection({ work, onWork, onOutcome }: IntentSectionProps) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const intent: Interpretation | null = work.intent;

  async function read() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${work.id}/interpret`, WorkActionResponse);
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const action = (
    <button type="button" disabled={busy} onClick={() => void read()} className="text-[13px] text-stone transition-colors hover:text-ink disabled:opacity-50">
      {intent ? "Read it again" : "Read this work"}
    </button>
  );

  if (!intent) {
    return (
      <Section>
        <SectionHead aside={action}>What Leveri understands</SectionHead>
        <p className="text-sm text-stone">
          Leveri has not read this work yet. Reading it proposes how the work would be performed, from the words and the
          records enclosed — and proposes nothing at all where it is not sure.
        </p>
        {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
      </Section>
    );
  }

  return (
    <>
      <Section>
        <SectionHead aside={action}>What Leveri understands</SectionHead>

        {/* The reading itself, set as the claim it is. */}
        <Statement>{intent.understanding}</Statement>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Marker tone={markerTone(intent.confidence.tone)} />
          <span className="text-[13px] text-ink">{intent.confidence.label}</span>
          <Citation>
            {intent.recognisedAs} · read by {intent.provenance.providerLabel} · {intent.provenance.interpretedAtText}
          </Citation>
        </div>

        {/* WHY LEVERI WILL NOT DO IT, in its own sentence, with what it can. */}
        {intent.unsupportedReason && (
          <div className="mt-4">
            <Notice tone="conflict">{intent.unsupportedReason}</Notice>
            {intent.offered.length > 0 && (
              <p className="mt-2.5 text-[13px] text-stone">
                What Leveri does perform: {intent.offered.map((o) => o.label).join(", ")}.
              </p>
            )}
          </div>
        )}

        {/* WHAT IT IS ASKING. A plan may carry a question too — knowing what
            the work is and still needing the accountant to choose the control
            line is the ordinary case, not a failure. */}
        {intent.question && (
          <div className="mt-4 border border-rule px-5 py-4">
            <Label>Leveri is asking</Label>
            <p className="mt-1.5 text-[15px] text-ink">{intent.question.question}</p>
            {intent.question.detail && <p className="mt-1.5 text-sm text-ink-soft">{intent.question.detail}</p>}
            {intent.question.options.length > 0 && (
              <ul className="mt-2.5 flex flex-col gap-1">
                {intent.question.options.map((option) => (
                  <li key={option.value} className="text-[13px] text-stone">{option.label}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
        {busy && <div className="mt-3"><Reading>Reading the work…</Reading></div>}
      </Section>

      {/* THE PLAN — a proposal. Nothing here has run. */}
      {intent.plan && (
        <Section>
          <SectionHead aside={<Citation>{intent.plan.pinnedVersionRef}</Citation>}>What Leveri would do</SectionHead>
          <p className="text-sm text-ink">{intent.plan.summary}</p>

          <div className="mt-4">
            <RuledList>
              {intent.plan.steps.map((step, index) => (
                <div key={step} className="grid grid-cols-[22px_minmax(0,1fr)] items-baseline gap-3 py-2.5">
                  <span className="text-[13px] text-stone [font-variant-numeric:tabular-nums]">{index + 1}</span>
                  <span className="text-sm text-ink">{step}</span>
                </div>
              ))}
            </RuledList>
          </div>

          {intent.plan.recordsNeeded.length > 0 && (
            <div className="mt-5">
              <Label>What it still needs</Label>
              <div className="mt-2">
                <Definitions
                  compact
                  items={intent.plan.recordsNeeded.map((kind) => ({ key: kind.role, term: kind.label, value: kind.purpose }))}
                />
              </div>
            </div>
          )}

          {/* THE LINE THIS CAPABILITY DRAWS, said BEFORE it runs — so nobody
              reads an explained difference as a validated one afterwards. */}
          <div className="mt-5">
            <Label>What it will not conclude</Label>
            <ul className="mt-2 flex flex-col gap-2">
              {intent.plan.boundaries.map((boundary) => (
                <li key={boundary} className="text-[13px] text-stone">{boundary}</li>
              ))}
            </ul>
          </div>

          <p className="mt-5 text-[13px] text-stone">
            Nothing above has run. The work begins when you start it.
          </p>
        </Section>
      )}
    </>
  );
}
